import React, { useState } from "react";
import FusionWhyPopover from "./FusionWhyPopover";
import { HelpDot } from "./FusionBits";
import {
  CHAIN_EFFECT_HINT, CHAIN_EFFECT_LABEL, CONSEQUENCE_HEADING, EXPECTED_HINT, EXPECTED_LABEL, EXPLOITED_HINT,
  FORECASTS_HEADING, NO_STATEMENTS_TEXT, SCENARIO_LABEL,
  formatMoney, severityClass, tierLabel,
} from "./fusionApi";

const HORIZONS = [["severity45", "45 days"], ["severity90", "90 days"], ["severity180", "180 days"]];

/** The first horizon at which the chain's forecast reaches its worst severity, e.g. "Critical by 90 days". */
export const consequenceHeadline = (c) => {
  if (!c) return null;
  const worst = c.severity180 || c.severity90 || c.severity45;
  if (!worst) return null;
  const at = HORIZONS.find(([k]) => c[k] === worst);
  return `${worst} by ${at ? at[1] : "180 days"}`;
};

/**
 * "How this chain plays out" (FUSION.md §13). On a listable chain: the derived consequence statements,
 * rendered verbatim with their confidence and evidence, the chain's impact once, and the three finance
 * figures, always apart: the expected annual loss (FAIR), the chain effect (the scenario uplift, an
 * estimate) and, when there is one, "if this chain is exploited" = the two added (§8.5; the FAIR figure is
 * never replaced). The risks' own forecasts (45 / 90 / 180 days, what will break, the 180-day outlook)
 * follow: collapsed under "From the individual risks" when the chain has its own statements, open
 * otherwise (walkthrough 2026-10-26).
 */
const FusionConsequence = ({ consequence, scenario, listable, labelOf }) => {
  const [whyLinks, setWhyLinks] = useState(null);
  const [forecastsOpen, setForecastsOpen] = useState(false);
  if (!consequence) return null;
  const statements = consequence.statements ?? [];
  const whatWillBreak = consequence.whatWillBreak ?? [];
  const hasForecast = HORIZONS.some(([k]) => consequence[k]);
  const hasStatements = listable && statements.length > 0;
  const showForecasts = !hasStatements || forecastsOpen;
  const uplift = listable && scenario && scenario.uplift > 0 ? scenario.uplift : null;
  return (
    <div className="rounded-lg border border-border bg-theme-secondary p-3 mb-2.5">
      <div className="font-semibold text-sm text-content-primary mb-1.5">{CONSEQUENCE_HEADING}</div>

      {listable && statements.length > 0 && (
        <>
          <ul className="space-y-1.5 mb-2">
            {statements.map((s, i) => (
              <li key={i} className="flex flex-wrap items-center gap-1.5 text-sm text-content-primary">
                <span>{s.mechanism} → {s.outcome}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${s.confidence === "confirmed" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700 border border-dashed border-amber-400"}`}>
                  {tierLabel(s.confidence)}
                </span>
                {s.upliftPercent > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded border border-border text-content-secondary">+{s.upliftPercent}% likelihood on connected risks</span>
                )}
                <button type="button" onClick={() => setWhyLinks(s.evidence ?? [])} className="text-xs text-blue-600 hover:underline">why?</button>
              </li>
            ))}
          </ul>
          <div className="text-sm text-content-secondary mb-2">{statements[0].impact}</div>
        </>
      )}
      {listable && statements.length === 0 && (
        <div className="text-sm text-content-secondary mb-2">{NO_STATEMENTS_TEXT}</div>
      )}

      {(hasStatements || uplift !== null) && (
        <div className="grid gap-1 mb-2" aria-label="Chain finance">
          <div className="text-sm text-content-primary flex items-center gap-1">
            <span>{EXPECTED_LABEL}: <b>{formatMoney(consequence.exposureMean)}</b></span>
            <HelpDot text={EXPECTED_HINT} />
          </div>
          {uplift !== null && (
            <>
              <div className="text-sm text-content-primary flex items-center gap-1 border border-dashed border-amber-300 rounded px-2 py-0.5 w-fit">
                <span>{CHAIN_EFFECT_LABEL}: +{formatMoney(uplift)}</span>
                <HelpDot text={CHAIN_EFFECT_HINT} />
              </div>
              <div className="text-sm text-content-primary flex items-center gap-1">
                <span>{SCENARIO_LABEL}: <b>{formatMoney(Number(consequence.exposureMean) + Number(uplift))}</b></span>
                <HelpDot text={EXPLOITED_HINT} />
              </div>
            </>
          )}
        </div>
      )}

      {hasStatements && (
        <button type="button" onClick={() => setForecastsOpen((o) => !o)} aria-expanded={forecastsOpen}
          className="text-xs font-semibold text-content-secondary hover:text-indigo-700 mb-1">
          {forecastsOpen ? "▾" : "▸"} {FORECASTS_HEADING}
        </button>
      )}
      {showForecasts && (<>
      {!hasStatements && <div className="text-xs font-semibold text-content-secondary mb-1">{FORECASTS_HEADING}</div>}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {HORIZONS.map(([k, label]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs">
            <span className="text-content-muted">{label}</span>
            {consequence[k]
              ? <span className={`px-2 py-0.5 rounded font-semibold ${severityClass(consequence[k])}`}>{consequence[k]}</span>
              : <span className="text-content-muted">—</span>}
          </span>
        ))}
        <span className="ml-auto text-xs text-red-800">Exposure if not attended <b>{formatMoney(consequence.exposureMean)}</b> a year</span>
      </div>
      {!hasForecast && <div className="text-xs text-content-muted mb-1">No severity forecast is recorded for these risks.</div>}
      {whatWillBreak.length > 0 && (
        <>
          <div className="text-xs font-semibold text-content-secondary mb-1">What will break</div>
          <ul className="list-disc pl-5 text-sm text-content-primary space-y-0.5">
            {whatWillBreak.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </>
      )}
      {consequence.outlook180 && (
        <div className="text-sm text-content-secondary mt-2"><span className="font-semibold">180-day outlook: </span>{consequence.outlook180}</div>
      )}
      </>)}

      {whyLinks && <FusionWhyPopover links={whyLinks} labelOf={labelOf} onClose={() => setWhyLinks(null)} />}
    </div>
  );
};

export default FusionConsequence;
